import { normaliseRow } from './awards.js';

/**
 * Offline match stats, for the award screens.
 *
 * Online the server keeps these — it has to, because a client that reported its
 * own damage figure would report whatever it liked. Offline there is nobody to
 * lie to, so this is much simpler: one row per actor, keyed by the actor object
 * itself, filled in from hooks at the places where things already happen.
 *
 * Deliberately NOT a second implementation of the awards. It produces the same
 * row shape `computeAwards` takes from the server, and the picking, the floors
 * and the tie-breaks all stay in awards.js.
 */
export class MatchStats {
  constructor() {
    this.rows = new Map();
    this._pos = new Map();
  }

  /**
   * A stable identity for an actor across the whole match.
   *
   * NOT the object, and not `Enemy.id`. Bomb defusal despawns every bot and
   * builds new ones at each round start, so both of those change seven times in
   * a best-of-seven and every bot's stats would reset with them. The mode
   * stamps a `slot` ("red0".."blue3") that survives, because the same slot is
   * the same soldier as far as a match is concerned.
   */
  static key(actor) {
    if (!actor) return null;
    if (!actor.isEnemy) return 'self';
    return actor.slot ?? `e${actor.id}`;
  }

  /** Row for an actor (the player controller or an Enemy), created on demand. */
  row(actor) {
    const k = MatchStats.key(actor);
    if (!k) return null;
    let r = this.rows.get(k);
    if (!r) {
      r = {
        id: k,
        actor,
        name: actor.isEnemy ? actor.name : 'YOU',
        self: !actor.isEnemy,
        team: actor.team === 'blue' ? 'blue' : 'red',
        op: actor.operator?.index ?? 0,
        kills: 0, deaths: 0, headshots: 0, damage: 0, dist: 0,
        plants: 0, defuses: 0, shots: 0, hits: 0,
        longestKill: 0, streak: 0, bestStreak: 0,
      };
      this.rows.set(k, r);
    }
    // Sides change between matches and the bot behind a slot is rebuilt every
    // round, so identity-adjacent fields are refreshed rather than cached.
    r.team = actor.team === 'blue' ? 'blue' : 'red';
    r.name = actor.isEnemy ? actor.name : 'YOU';
    if (actor.operator) r.op = actor.operator.index;
    return r;
  }

  onShot(actor) { const r = this.row(actor); if (r) r.shots++; }

  /**
   * @param {number} amount damage the victim actually had left to lose — see
   *   awards.js on why the finisher does not out-score the four hits before it.
   */
  onDamage(attacker, victim, amount, part, dist = 0) {
    if (!attacker || attacker === victim) return;
    const r = this.row(attacker);
    if (!r) return;
    // Friendly fire is not an achievement.
    if (attacker.team && victim?.team && attacker.team === victim.team) return;
    r.damage += Math.max(0, amount);
    r.hits++;
    if (part === 'head') r.headshots++;
    void dist;
  }

  onKill(attacker, victim, dist = 0) {
    const v = this.row(victim);
    if (v) { v.deaths++; v.streak = 0; }
    if (!attacker || attacker === victim) return;
    const r = this.row(attacker);
    if (!r) return;
    r.kills++;
    r.streak++;
    if (r.streak > r.bestStreak) r.bestStreak = r.streak;
    if (dist > r.longestKill) r.longestKill = dist;
  }

  onPlant(actor) { const r = this.row(actor); if (r) r.plants++; }
  onDefuse(actor) { const r = this.row(actor); if (r) r.defuses++; }

  /**
   * Ground covered, sampled rather than hooked.
   *
   * Movement has no single choke point offline — the player integrates in
   * PlayerController, bots in Enemy._integrate — so this compares positions
   * frame to frame instead. Horizontal only, and a jump of over three metres in
   * one frame is discarded as a respawn or a teleport rather than a sprint.
   */
  sample(actors) {
    for (const a of actors) {
      if (!a) continue;
      const p = a.position ?? a.root?.position;
      if (!p) continue;
      const k = MatchStats.key(a);
      const prev = this._pos.get(k);
      if (prev) {
        const d = Math.hypot(p.x - prev.x, p.z - prev.z);
        if (d < 3) { const r = this.row(a); if (r) r.dist += d; }
        prev.x = p.x; prev.z = p.z;
      } else {
        this._pos.set(k, { x: p.x, z: p.z });
      }
    }
  }

  /** Award rows, in the shape awards.js and the screen both want. */
  toRows() {
    return [...this.rows.values()].map((r) => ({ ...normaliseRow(r), self: r.self, name: r.name }));
  }

  reset() { this.rows.clear(); this._pos.clear(); }
}
